import React, { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import userStore from '../../store/userStore';
import AIAgentStatus from './components/AIAgentStatus';
import AIAgentStats from './components/AIAgentStats';
import AIAgentPrompts from './components/AIAgentPrompts';
import AIAgentLogs from './components/AIAgentLogs';
import AIAgentPlans from './components/AIAgentPlans';
import AIAgentReview from './components/AIAgentReview';
import AIAgentRendimiento from './components/AIAgentRendimiento';
import ArCompaniesAgent from './components/ArCompaniesAgent';

// "Revisión" va la segunda: es lo único de esta página que tiene trabajo
// pendiente esperando a una persona. El resto se consulta cuando hace falta;
// esto hay que mirarlo.
const TABS = [
  { id: 'status', label: 'Estado' },
  { id: 'review', label: 'Revisión' },
  // Va detrás de Revisión porque es su pareja: una ensena lo que espera, la
  // otra en que acabo lo que ya paso por ahi.
  { id: 'rendimiento', label: 'Rendimiento' },
  { id: 'prompts', label: 'Prompts' },
  { id: 'plans', label: 'Planes' },
  { id: 'logs', label: 'Registros' },
];

/**
 * La revisión del admin, partida por fase (docs/ar-revision-por-fase-plan.md):
 * «Recobro» es su trabajo; «AR» es supervisar lo que revisan los clientes, sin
 * decidirlo. El `key` vuelve a montar la cola al cambiar: filtros y caso
 * abierto son de una fase, no se arrastran a la otra.
 */
export const AdminReview = () => {
  const [phase, setPhase] = useState('recovery');
  const opciones = [
    { id: 'recovery', label: 'Recobro' },
    { id: 'ar', label: 'AR (supervisión)' },
  ];
  return (
    <div className='space-y-4'>
      <div className='inline-flex rounded-lg border border-slate-200 bg-white p-1' role='tablist'>
        {opciones.map(o => (
          <button
            key={o.id}
            type='button'
            role='tab'
            aria-selected={phase === o.id}
            onClick={() => setPhase(o.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              phase === o.id ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {phase === 'ar' && (
        <p className='text-sm text-slate-500'>
          Lo AR lo revisa cada cliente. Aquí se ve para comprobar que la IA funciona bien, pero no se decide.
        </p>
      )}
      {/* Qué ha soltado cada cliente y el interruptor de ClaimUp (06-reparto §4.4). */}
      {phase === 'ar' && <ArCompaniesAgent />}
      <AIAgentReview key={phase} phase={phase} readOnly={phase === 'ar'} showSettings={phase === 'recovery'} />
    </div>
  );
};

const AIAgentPage = () => {
  const user = userStore(state => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'status';
  const [activeTab, setActiveTab] = useState(initialTab);

  if (user.role !== 'admin') {
    return <Navigate to='/dashboard' replace />;
  }

  const handleTabChange = tab => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  return (
    <MainLayout>
      <div className='bg-white shadow-lg rounded-lg border border-slate-200'>
        <header className='px-5 py-4 border-b border-slate-100'>
          <h1 className='text-2xl font-semibold text-slate-900'>AI Agent</h1>
        </header>

        <div className='border-b border-slate-200'>
          <nav className='flex space-x-6 px-5'>
            {TABS.map(tab => (
              <button
                key={tab.id}
                type='button'
                onClick={() => handleTabChange(tab.id)}
                className={`py-3 text-sm font-medium border-b-2 transition-colors outline-none focus:outline-none ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className='p-5'>
          {activeTab === 'status' && (
            <div className='space-y-6'>
              <AIAgentStatus />
              <AIAgentStats />
            </div>
          )}
          {activeTab === 'review' && <AdminReview />}
          {activeTab === 'rendimiento' && <AIAgentRendimiento />}
          {activeTab === 'prompts' && <AIAgentPrompts />}
          {activeTab === 'plans' && <AIAgentPlans />}
          {activeTab === 'logs' && <AIAgentLogs />}
        </div>
      </div>
    </MainLayout>
  );
};

export default AIAgentPage;
